import { useEffect, useState } from "react";
import { clientApi, getApiErrorMessage } from "@/lib/api";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatCurrency } from "@/lib/format";
import { toast } from "sonner";
import { Users, UserPlus, Check, Trash2 } from "lucide-react";

export default function Family() {
  const [links, setLinks] = useState([]);
  const [pf, setPf] = useState(null);
  const [email, setEmail] = useState("");
  const [relationship, setRelationship] = useState("Spouse");
  const [loading, setLoading] = useState(true);

  const load = () => Promise.all([
    clientApi.get("/family").then((r) => setLinks(r.data)),
    clientApi.get("/family/portfolio").then((r) => setPf(r.data)),
  ]).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const invite = async () => {
    try {
      await clientApi.post("/family/invite", { email, relationship });
      toast.success("Invite sent — they can accept it from their Family page");
      setEmail("");
      load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };
  const accept = async (id) => { await clientApi.post(`/family/links/${id}/accept`); toast.success("Link accepted"); load(); };
  const remove = async (id) => { await clientApi.delete(`/family/links/${id}`); load(); };

  const combined = pf?.combined;
  return (
    <ClientLayout>
      <div className="mb-6">
        <h1 className="flex items-center gap-2 font-heading text-3xl text-[var(--brand)]"><Users className="h-7 w-7" /> Family Portfolio</h1>
        <p className="mt-1 text-sm text-[var(--ink-muted)]">Link family accounts and see everyone's wealth in one view.</p>
      </div>

      {combined && combined.members_count > 1 && (
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[["Family Value", formatCurrency(combined.current_value), "family-combined-value"],
            ["Invested", formatCurrency(combined.invested)],
            ["Overall Gain", `${combined.gain >= 0 ? "+" : ""}${formatCurrency(combined.gain)} (${combined.gain_pct}%)`],
            ["Members", combined.members_count]].map(([label, value, tid]) => (
            <Card key={label} className="glass-card border-0"><CardContent className="p-4">
              <div className="text-xs text-[var(--ink-muted)]">{label}</div>
              <div className="mt-1 font-heading text-xl text-[var(--brand)]" data-testid={tid}>{value}</div>
            </CardContent></Card>
          ))}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <div className="space-y-4">
          {(pf?.members || []).map((m) => (
            <Card key={m.user_id} className="border-[var(--glass-border)]" data-testid={`family-member-${m.client_code}`}>
              <CardHeader className="pb-2">
                <div className="flex flex-col items-start gap-1 min-[380px]:flex-row min-[380px]:items-center min-[380px]:justify-between">
                  <CardTitle className="font-heading text-base text-[var(--brand)]">{m.name} {m.is_self && <span className="text-xs text-[var(--ink-muted)]">(you)</span>}</CardTitle>
                  <div className="text-sm font-semibold">{formatCurrency(m.summary?.current_value)}</div>
                </div>
                <div className="text-xs text-[var(--ink-muted)]">{m.client_code} · XIRR {m.summary?.active_xirr ?? "—"}% · Gain {m.summary?.gain_pct ?? 0}%</div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-[var(--glass-border)]">
                  {(m.holdings || []).slice(0, 5).map((h) => (
                    <div key={h.scheme_code} className="flex items-center justify-between gap-3 px-4 py-2 text-xs">
                      <span className="min-w-0 flex-1 truncate">{h.scheme_name}</span>
                      <span className="font-medium">{formatCurrency(h.current_value)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="glass-card h-fit border-0">
          <CardHeader><CardTitle className="flex items-center gap-2 font-heading text-base text-[var(--brand)]"><UserPlus className="h-4 w-4" /> Link a family member</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Input placeholder="Their registered email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="family-email-input" />
            <select className="w-full rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={relationship} onChange={(e) => setRelationship(e.target.value)} data-testid="family-relationship-select">
              {["Spouse", "Parent", "Child", "Sibling", "Family"].map((r) => <option key={r}>{r}</option>)}
            </select>
            <Button className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" disabled={!email} onClick={invite} data-testid="family-invite-button">Send invite</Button>
            {links.length > 0 && (
              <div className="space-y-2 pt-2">
                <div className="text-xs font-medium text-[var(--ink-muted)]">Links</div>
                {links.map((l) => (
                  <div key={l.id} className="flex items-center justify-between gap-2 rounded-lg border border-[var(--glass-border)] p-2 text-xs" data-testid={`family-link-${l.id}`}>
                    <div className="min-w-0">
                      <div className="font-medium">{l.incoming ? l.from_name : l.to_name}</div>
                      <div className="text-[var(--ink-muted)]">{l.relationship} · {l.status}</div>
                    </div>
                    <div className="flex gap-1">
                      {l.incoming && l.status === "PENDING" && (
                        <Button size="sm" variant="outline" className="h-7 rounded-full text-emerald-700" onClick={() => accept(l.id)} data-testid={`family-accept-${l.id}`}><Check className="h-3.5 w-3.5" /></Button>
                      )}
                      <Button size="sm" variant="ghost" className="h-7 text-red-500" onClick={() => remove(l.id)} data-testid={`family-remove-${l.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </ClientLayout>
  );
}
